import { Component, OnInit, Input, effect } from '@angular/core';
import {
  Student,
  AdmissionsService,
  CNexusEventResultResponse,
  UIConfiguration,
  ReminderRequested,
  DispositionService,
  ValidDispositionRequested,
  DispositionReasonValue,
  ScheduleReminder,
  PropertyValue
} from '../../../lib';

import { GenesysSparkWrapperService } from '../genesys-spark-wrapper.service';
import { AdmissionUICommonDataService } from '../admission-ui-common-data.service';
import { AdmissionUIMessengerService } from '../admission-ui-messenger.service';
import { forkJoin, Observable, of} from 'rxjs';
import { LeadConversationState, LeadConversationStateStore } from '../../dialpad/leadConversation.state';

@Component({
  selector: 'app-schedule-screen',
  templateUrl: './schedule-screen.component.html',
  styleUrl: '../admission-ui.component.scss'
})
export class ScheduleScreenComponent implements OnInit {
  @Input() buttonType: string = 'primary';
  @Input() previousUserCallStatus: string = 'Available';
  @Input() callSkills: string[] = [];

  isLoading: boolean = true;
  studentData: Student | undefined;
  showStudentId: boolean = false;
  cNexusEventResult: CNexusEventResultResponse[] | undefined;
  studentFinanceMsg: string = '';
  otherInfoMsg: string = '';
  iDashURL: string = '';
  CNexusEventAppliesToId: number = 4;
  CNexusEvents: CNexusEventResultResponse[] | undefined;
  uiConfiguration: UIConfiguration[] | undefined;
  dropdownChoice: CNexusEventResultResponse | null = null;
  comments: string = '';
  callStatus: string = 'Waiting';
  callType: string = 'Outgoing';

  isConsentConfirmed: boolean | undefined | null;

  reminderRequest: ReminderRequested | undefined;
  startDate: Date | undefined;
  reminderLength: number | undefined;

  scheduleDate!: string;
  minDate!: string;
  scheduleTime: string = '09:15';

  timePickerElement: any | null;

  staffId: number | undefined;
  accountId: number | undefined;
  dialerContactId: number | undefined;
  telecomDialerCampaignName : string | null | undefined;
  selectedDispositionOption: DispositionReasonValue | undefined;
  conversationId: string | undefined = '';
  emailConversationId: string | undefined;
  private leadConversationState: LeadConversationState = {};
  contactedEngaged!: PropertyValue;
  contactedNotEngaged!: PropertyValue;
  isEngaged: boolean = true;
  isDateError: boolean = false;
  dispositionTimer: number = 0;
  dispositionNotifyNoActionLimit: number | undefined;
  bannerWarning: boolean = false;
  displayOtherInfo: boolean = true;
  autoDispositionLimit : number | undefined;
  constructor(
    private readonly genesysSparkWrapper: GenesysSparkWrapperService,
    private readonly admissionService: AdmissionsService,
    private readonly messenger: AdmissionUIMessengerService,
    private readonly commonDataService: AdmissionUICommonDataService,
    private readonly dispositionService: DispositionService,
    private readonly leadConversationStateStore: LeadConversationStateStore
  ) {
    effect(() => {
      this.leadConversationState = this.leadConversationStateStore.getState()();
      this.callStatus = this.leadConversationState.CallStatus ?? 'Not Set';
      this.studentData = this.leadConversationState.Student;
      this.conversationId = this.leadConversationState.Call?.id;
      this.emailConversationId = this.leadConversationState.Email?.id
      this.callType = this.leadConversationState.CallType ?? 'OutgoingCall';
      this.previousUserCallStatus = this.leadConversationState?.PreviousUserStatus ?? "Available";
      this.dialerContactId = this.leadConversationState.DialerContactId ? parseInt(this.leadConversationState.DialerContactId) : undefined;
      this.telecomDialerCampaignName = this.leadConversationState.TelecomDialerCampaignName;
    });
  }

  ngOnInit() {
    this.genesysSparkWrapper.register().then(() => {
      this.studentData = this.leadConversationState.Student;

      this.messenger.consentConfirmed.subscribe(consentConfirmed => {
        this.isConsentConfirmed = consentConfirmed;
      });

      const now = new Date();
      this.scheduleDate = this.minDate = now.toISOString().split('T')[0];

      this.loadUIConfiguration().subscribe();

      this.admissionService.apiCNexusEventResultGet(true).subscribe(data => {
        this.cNexusEventResult = data;
        for (const item of this.cNexusEventResult) {
          if (item?.appliesTo) {
            if (item?.appliesTo?.id == this.CNexusEventAppliesToId) {
              if (this.CNexusEvents) {
                this.CNexusEvents.push(item);
              }
              else {
                this.CNexusEvents = [item];
              }
            }
          }
        }
      });

      if (this.studentData?.needsToContactStudentFinance) {
        this.studentFinanceMsg = 'Needs to speak to Student Finance.'
      }

      this.isLoading = false;
    });

    this.commonDataService.getAccountAndStaffId().subscribe(IdList => {
      this.staffId = IdList.staffId;
      this.accountId = IdList.accountId;
    });

    setInterval(() => {
      this.dispositionTimer = this.leadConversationStateStore.getDispositionDuration();
      if (this.dispositionNotifyNoActionLimit && this.dispositionTimer >= this.dispositionNotifyNoActionLimit) {
        this.bannerWarning = true;
      }
    }, 1000);
  }


  private loadUIConfiguration(): Observable<null> {
    return new Observable<null>(obs => {
      this.commonDataService.getUIConfigurationAndSchoolStatuses().subscribe(data => {
        this.uiConfiguration = data.uiConfiguration;
        if (this.uiConfiguration) {
          this.setupUIConfiguration();
        }
        obs.next(null);
        obs.complete();
      });
    })
  }

  setupUIConfiguration() {
    if (this.uiConfiguration) {
      const appliesTo = this.uiConfiguration.find(obj => obj.key === 'DispositionWindow.InterviewResult.AppliesTo')?.value;
      if (appliesTo) {
        this.CNexusEventAppliesToId = parseInt(appliesTo);
      }

      this.showStudentId = this.uiConfiguration.find(obj => obj.key === 'DispositionWindow.ShowStudentId.Enable')?.value === '1'

      const reminderLength = this.uiConfiguration.find(obj => obj.key === 'Schedule.InterviewReminder.Minutes')?.value;
      if (reminderLength) {
        this.reminderLength = parseInt(reminderLength);
      } else {
        this.reminderLength = 30;
      }
      const iDashBaseURL = this.uiConfiguration?.find(obj => obj.key === 'LsIdash.Url')?.value;
      if (iDashBaseURL && this.studentData?.id !== undefined) {
        this.iDashURL = iDashBaseURL + this.studentData?.id;
      }
      const notifyNoActionTime = this.uiConfiguration?.find(obj => obj.key === 'Disposition.NotifyNoActionSeconds')?.value;
      if (notifyNoActionTime) {
        this.dispositionNotifyNoActionLimit = parseInt(notifyNoActionTime);
      }
      const autoDispositionTime = this.uiConfiguration?.find(obj => obj.key === 'Disposition.AutoDispositionSeconds')?.value;
      if (autoDispositionTime) {
        this.autoDispositionLimit = parseInt(autoDispositionTime);
      }
    }
  }

  checkIsSelected() {
    if (!this.isEngaged) {
      this.comments = '';
      this.dropdownChoice = null;
    }
  }

  scheduleDateChanged(event: any) {
    this.scheduleDate = event.detail;
  }

  scheduleTimeChanged(event:any){
    this.scheduleTime = event.currentTarget.value;
  }

  dropdownSelected(event: any) {
    this.dropdownChoice = event;
  }

  submit() {
    // Use the already loaded property values from the common data service
    this.contactedEngaged = this.commonDataService.contactedEngaged!;
    this.contactedNotEngaged = this.commonDataService.contactedNotEngaged!;
    let currentPhone: string = this.studentData?.phoneDigits!;
    if (new Date(this.scheduleDate) < new Date(this.minDate)) {
      this.isDateError = true;
    }
    else {
      this.isDateError = false;
      if (this.isEngaged) {
        this.selectedDispositionOption = { id: this.contactedEngaged.id, value: this.contactedEngaged.value };
      }
      else {
        this.selectedDispositionOption = { id: this.contactedNotEngaged.id, value: this.contactedNotEngaged.value };
      }
      const consent = this.isConsentConfirmed ?? undefined
      let validDispostionRequested: ValidDispositionRequested = {
        conversationId: this.conversationId!,
        reason: { id: this.selectedDispositionOption?.id, value: this.selectedDispositionOption?.value },
        dialerContactId: this.dialerContactId,
        interactionType: this.callType,
        skills: this.callSkills,
        interviewResult: this.dropdownChoice?.code,
        phone: this.studentData?.phoneDigits,
        syStudentId: this.studentData?.id,
        sySchoolFields: this.studentData?.schoolFields,
        telecomDialerCampaignName: this.telecomDialerCampaignName,
        tcpaConsentGiven: consent,
        accountId: this.accountId,
        syStaffId: this.staffId,
        syStudentInquiryId: this.studentData?.inquiries?.find(obj => obj.isCurrent === true)?.id,
        admissionsRepresentativeId: this.studentData?.admissionsRepresentative ? this.studentData?.admissionsRepresentative.id : undefined
      };

      if (this.isEngaged) {
        const startDate = new Date(this.scheduleDate + 'T' + this.scheduleTime + ':00.000Z');
        const endDate = new Date(startDate);
        endDate.setMinutes(endDate.getMinutes() + (this.reminderLength || 0));

        const reminderBody = this.iDashURL + '\n' + this.comments;

        const scheduleReminder: ScheduleReminder = {
          subject: String(this.studentData?.id),
          body: reminderBody,
          startTime: startDate.toISOString().slice(0, 19),
          endTime: endDate.toISOString().slice(0, 19)
        }
        validDispostionRequested.scheduleReminder = scheduleReminder;
      }


      const dispositionPost$ = this.dispositionService.apiDispositionValidDispositionRequestedPost(validDispostionRequested);

      const disconnectEmail$ = this.emailConversationId
        ? this.leadConversationStateStore.disconnectDispostionedEmail()
        : of(null);
      this.leadConversationStateStore.endInteraction(currentPhone);
      let previousUserCallStatus = this.previousUserCallStatus;
      forkJoin([dispositionPost$, disconnectEmail$]).subscribe(() => {
        this.commonDataService.toggleUserStatus(previousUserCallStatus).subscribe(() => { });

      });
    }
  }

  cancel() {
    this.messenger.setPage('Disposition');
  }
}
